/**
 * HTML 데이터 추출기
 * 학습 자료 HTML에서 데이터, 스타일, 구조 정보를 분리 추출
 */

// 템플릿 타입 감지
export const detectTemplateType = (htmlContent) => {
  const content = htmlContent.toLowerCase();
  
  if (content.includes('flashcard') || content.includes('단어카드')) {
    return 'flashcard';
  } else if (content.includes('quiz') || content.includes('퀴즈')) {
    return content.includes('객관식') ? 'multiple-choice-quiz' : 'short-answer-quiz';
  } else if (content.includes('passage') || content.includes('지문')) {
    return 'passage';
  } else if (content.includes('listening') || content.includes('듣기')) {
    return 'listening';
  } else if (content.includes('shadowing') || content.includes('쉐도잉')) {
    return 'shadowing';
  } else if (content.includes('career') || content.includes('진로')) {
    return 'career-path';
  }
  
  return 'unknown';
};

// JavaScript에서 데이터 추출
export const extractJavaScriptData = (htmlContent) => {
  try {
    const scriptMatch = htmlContent.match(/<script[^>]*>([\s\S]*?)<\/script>/gi);
    if (!scriptMatch) return {};

    let extractedData = {};
    
    scriptMatch.forEach(script => {
      const scriptContent = script.replace(/<\/?script[^>]*>/gi, '');
      
      // 패턴 1: JSON 데이터 스크립트 (template-data)
      if (script.includes('id="template-data"') || script.includes('type="application/json"')) {
        try {
          const jsonData = JSON.parse(scriptContent.trim());
          console.log('📋 JSON 템플릿 데이터 발견:', jsonData);
          
          if (jsonData.words) {
            extractedData.vocabularyData = jsonData.words;
            console.log('✅ JSON에서 단어 데이터 추출 완료:', jsonData.words.length, '개');
          }
          
          if (jsonData.questions) {
            extractedData.questionsData = jsonData.questions;
          }
          
          if (jsonData.style) {
            extractedData.styleData = jsonData.style;
          }
          
          if (jsonData.layout) {
            extractedData.layoutData = jsonData.layout;
            console.log('✅ JSON에서 레이아웃 데이터 추출 완료:', jsonData.layout);
          }
          
          return; // JSON 데이터를 찾았으면 다른 패턴은 확인하지 않음
        } catch (e) {
          console.warn('JSON 템플릿 데이터 파싱 실패:', e);
        }
      }
      
      // 패턴 2: 일반적인 변수 배열 패턴
      let wordsData = null;
      const wordsMatch = scriptContent.match(/(?:words|vocabulary|단어)\s*[:=]\s*(\[[\s\S]*?\])/i);
      if (wordsMatch) {
        try {
          wordsData = JSON.parse(wordsMatch[1]);
        } catch (e) {
          console.warn('단어 데이터 파싱 실패:', e);
        }
      }
      
      // 패턴 3: {{ WORD_DATA }} 플레이스홀더 - 기본 샘플 데이터 사용
      else if (scriptContent.includes('{{ WORD_DATA }}')) {
        console.log('📋 {{ WORD_DATA }} 플레이스홀더 발견 - 기본 샘플 데이터 사용');
        wordsData = [
          {"word": "apple", "meaning": "사과", "example": "I eat an apple every morning."},
          {"word": "book", "meaning": "책", "example": "She is reading a book in the library."},
          {"word": "computer", "meaning": "컴퓨터", "example": "I use my computer for work every day."},
          {"word": "dog", "meaning": "개", "example": "My dog loves to play in the park."},
          {"word": "English", "meaning": "영어", "example": "Learning English is very important."}
        ];
      }
      
      if (wordsData) {
        extractedData.vocabularyData = wordsData;
        console.log('✅ 단어 데이터 추출 완료:', wordsData.length, '개');
      }
      
      // 문제 데이터 추출
      const questionsMatch = scriptContent.match(/(?:questions|problems|문제)\s*[:=]\s*(\[[\s\S]*?\])/i);
      if (questionsMatch) {
        try {
          extractedData.questionsData = JSON.parse(questionsMatch[1]);
        } catch (e) {
          console.warn('문제 데이터 파싱 실패:', e);
        }
      }
      
      // 지문 데이터 추출
      const passageMatch = scriptContent.match(/(?:passage|text|지문)\s*[:=]\s*["`']([^"`']*)["`']/i);
      if (passageMatch) {
        extractedData.passageData = {
          content: passageMatch[1],
          title: extractPassageTitle(scriptContent)
        };
      }
      
      // 설정 데이터 추출
      const settingsMatch = scriptContent.match(/(?:settings|config|설정)\s*[:=]\s*(\{[\s\S]*?\})/i);
      if (settingsMatch) {
        try {
          extractedData.settingsData = JSON.parse(settingsMatch[1]);
        } catch (e) {
          console.warn('설정 데이터 파싱 실패:', e);
        }
      }
    });
    
    return extractedData;
  } catch (error) {
    console.error('JavaScript 데이터 추출 실패:', error);
    return {};
  }
};

// 지문 제목 추출 보조 함수
const extractPassageTitle = (scriptContent) => {
  const titleMatch = scriptContent.match(/(?:title|제목)\s*[:=]\s*["`']([^"`']*)["`']/i);
  return titleMatch ? titleMatch[1] : '';
};

// HTML DOM에서 데이터 추출
export const extractDOMData = (htmlContent) => {
  try {
    // 임시 DOM 생성
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlContent, 'text/html');
    
    const extractedData = {};
    
    // 단어카드 데이터 추출
    const flashcards = doc.querySelectorAll('.flashcard, .word-card, [data-word]');
    if (flashcards.length > 0) {
      extractedData.vocabularyData = Array.from(flashcards).map(card => ({
        word: card.querySelector('.word, .front, [data-word]')?.textContent?.trim() || '',
        meaning: card.querySelector('.meaning, .back, [data-meaning]')?.textContent?.trim() || '',
        example: card.querySelector('.example, [data-example]')?.textContent?.trim() || ''
      }));
    }
    
    // 퀴즈 문제 데이터 추출
    const questions = doc.querySelectorAll('.question, .quiz-item, [data-question]');
    if (questions.length > 0) {
      extractedData.questionsData = Array.from(questions).map((q, index) => {
        const questionText = q.querySelector('.question-text, h3, p')?.textContent?.trim() || '';
        const choices = Array.from(q.querySelectorAll('.choice, .option, input[type="radio"] + label')).map(choice => 
          choice.textContent?.trim() || ''
        );
        const answer = q.querySelector('[data-answer]')?.dataset.answer || 
                      q.querySelector('.answer')?.textContent?.trim() || '';
        
        return {
          id: index + 1,
          question: questionText,
          choices: choices.length > 0 ? choices : undefined,
          answer: answer,
          type: choices.length > 0 ? 'multiple-choice' : 'short-answer'
        };
      });
    }
    
    // 지문 데이터 추출
    const passageElement = doc.querySelector('.passage, .reading-content, [data-passage]');
    if (passageElement) {
      extractedData.passageData = {
        title: doc.querySelector('.passage-title, h1, h2')?.textContent?.trim() || '',
        content: passageElement.textContent?.trim() || ''
      };
    }
    
    // 오디오 데이터 추출
    const audioElements = doc.querySelectorAll('audio, [data-audio]');
    if (audioElements.length > 0) {
      extractedData.audioData = Array.from(audioElements).map(audio => ({
        src: audio.src || audio.dataset.audio || '',
        script: audio.dataset.script || '',
        title: audio.dataset.title || ''
      }));
    }
    
    return extractedData;
  } catch (error) {
    console.error('DOM 데이터 추출 실패:', error);
    return {};
  }
};

// CSS 스타일 데이터 추출
export const extractStyleData = (htmlContent) => {
  try {
    const styleMatch = htmlContent.match(/<style[^>]*>([\s\S]*?)<\/style>/gi);
    if (!styleMatch) return {};
    
    let combinedStyles = '';
    styleMatch.forEach(style => {
      combinedStyles += style.replace(/<\/?style[^>]*>/gi, '') + '\n';
    });
    
    return {
      cssContent: combinedStyles,
      colorScheme: extractColorScheme(combinedStyles),
      layout: extractLayoutInfo(combinedStyles)
    };
  } catch (error) {
    console.error('스타일 데이터 추출 실패:', error);
    return {};
  }
};

// 색상 체계 추출
const extractColorScheme = (cssContent) => {
  const colors = [];
  const colorMatches = cssContent.match(/(#[0-9a-fA-F]{3,6}|rgb\([^)]+\)|rgba\([^)]+\))/g);
  if (colorMatches) {
    colors.push(...colorMatches);
  }
  return [...new Set(colors)]; // 중복 제거
};

// 레이아웃 정보 추출
const extractLayoutInfo = (cssContent) => {
  return {
    hasGrid: cssContent.includes('display: grid') || cssContent.includes('grid-template'),
    hasFlex: cssContent.includes('display: flex') || cssContent.includes('flex-direction'),
    hasAnimation: cssContent.includes('@keyframes') || cssContent.includes('animation:'),
    responsive: cssContent.includes('@media')
  };
};

// 메인 추출 함수
export const parseLearnignHTML = (htmlContent) => {
  try {
    const templateType = detectTemplateType(htmlContent);
    const jsData = extractJavaScriptData(htmlContent);
    const domData = extractDOMData(htmlContent);
    const styleData = extractStyleData(htmlContent);
    
    // 데이터 병합 (JavaScript 데이터 우선)
    const vocabularyData = jsData.vocabularyData || domData.vocabularyData || [];
    const questionsData = jsData.questionsData || domData.questionsData || [];
    
    const mergedData = {
      vocabularyData: vocabularyData,
      words: vocabularyData, // words 필드로도 매핑 (AI편집 템플릿용)
      questionsData: questionsData,
      questions: questionsData, // questions 필드로도 매핑
      passageData: jsData.passageData || domData.passageData || {},
      audioData: jsData.audioData || domData.audioData || [],
      settingsData: jsData.settingsData || {},
      styleData: styleData,
      templateType: templateType,
      originalHTML: htmlContent
    };
    
    console.log('📊 HTML 데이터 추출 완료:', {
      templateType,
      vocabularyCount: mergedData.vocabularyData.length,
      questionsCount: mergedData.questionsData.length,
      hasPassage: !!mergedData.passageData.content,
      hasAudio: mergedData.audioData.length > 0,
      hasStyles: !!styleData.cssContent
    });
    
    return mergedData;
  } catch (error) {
    console.error('❌ HTML 파싱 실패:', error);
    throw new Error('HTML 파일을 분석할 수 없습니다: ' + error.message);
  }
};

// 데이터 유효성 검사
export const validateExtractedData = (extractedData) => {
  const issues = [];
  
  if (!extractedData.templateType || extractedData.templateType === 'unknown') {
    issues.push('템플릿 타입을 감지할 수 없습니다.');
  }
  
  if (extractedData.vocabularyData.length === 0 && 
      extractedData.questionsData.length === 0 && 
      !extractedData.passageData.content) {
    issues.push('학습 데이터를 찾을 수 없습니다.');
  }
  
  return {
    isValid: issues.length === 0,
    issues: issues,
    dataTypes: {
      hasVocabulary: extractedData.vocabularyData.length > 0,
      hasQuestions: extractedData.questionsData.length > 0,
      hasPassage: !!extractedData.passageData.content,
      hasAudio: extractedData.audioData.length > 0,
      hasStyles: !!extractedData.styleData.cssContent
    }
  };
};